import React, { useContext, FunctionComponent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CookieConsentService } from '@inter-ikea-kompis/services';
import { useServiceSettings } from './serviceSettings';
import { isKiosk, isDevelopment } from 'utils/platform';
import { e2e } from 'utils/queryUtils';
import { KompisSettings } from '@inter-ikea-storageone/range';

type Consent = {
  [level: number]: boolean;
};

export type WithCookieConsent = (
  consentLevel: number,
  callback: (() => void) | (() => Promise<void>)
) => Promise<void>;

export type HasCookieConsent = (consentLevel: CookieConsentLevels) => Promise<boolean>;

type CookieConsentProviderProps = {
  settings?: Consent;
  withCookieConsent: WithCookieConsent;
  hasCookieConsent: HasCookieConsent;
};

export const CookieConsent = React.createContext<CookieConsentProviderProps>({
  settings: undefined,
  withCookieConsent: async () => {},
  hasCookieConsent: async () => Promise.reject()
});

export const useCookieConsent = () => useContext(CookieConsent);

export enum CookieConsentLevels {
  /** Technical cookies help to make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies. */
  TECHNICAL = 1,
  /** Analytics cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously. */
  ANALYTICAL = 2,
  /** Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in. */
  PREFERENCE = 3,
  /** Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers. */
  MARKETING = 4
}

const consentLevels = [
  CookieConsentLevels.TECHNICAL,
  CookieConsentLevels.ANALYTICAL,
  CookieConsentLevels.PREFERENCE,
  CookieConsentLevels.MARKETING
];

const getConsentLevels = async (kompisSettings: KompisSettings): Promise<Consent> => {
  const service = new CookieConsentService(kompisSettings);

  const consent = await Promise.all(
    consentLevels.map(level => {
      const checkLevel = async () => {
        return {
          [level]: await service.hasCookieConsent(level)
        };
      };
      return checkLevel();
    })
  ).catch(() => {
    // eslint-disable-next-line no-console
    console.warn('Cant fetch consent, falling back to level 1');
    return consentLevels.map(level => {
      return { [level]: CookieConsentLevels.TECHNICAL === level };
    });
  });

  return Object.assign({}, ...consent);
};

export const CookieConsentProvider: FunctionComponent<{ children: React.ReactNode }> = ({
  children
}) => {
  const { kompisSettings } = useServiceSettings();
  const { data, error, isPending } = useQuery({
    queryFn: () => getConsentLevels(kompisSettings),
    queryKey: ['cookieConsent'],
    refetchOnWindowFocus: false,
    staleTime: Infinity
  });

  if (isPending || !data) {
    return null;
  }

  if (error) {
    return <div>Something went wrong</div>;
  }

  const withCookieConsent: WithCookieConsent = async (consentLevel, callback) => {
    if (isDevelopment() || isKiosk() || data[consentLevel]) return callback();
  };

  const hasCookieConsent = async (consentLevel: CookieConsentLevels) => {
    if (isDevelopment() || isKiosk() || e2e()) {
      return true;
    }

    return data[consentLevel];
  };

  if (data) {
    return (
      <CookieConsent.Provider value={{ settings: data, withCookieConsent, hasCookieConsent }}>
        {children}
      </CookieConsent.Provider>
    );
  }

  return null;
};
